import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import TutorCard from './TutorCard';
import { 
  FaFilter, FaSearch, FaMapMarkerAlt, FaGraduationCap, 
  FaMoneyBill, FaSort, FaStar, FaClock, FaTimes,
  FaUserGraduate, FaBookOpen, FaCheckCircle, FaUsers,
  FaCaretDown, FaCaretUp
} from 'react-icons/fa';
import Navbar from '../layout/Navbar';

const TutorSearch = () => {
  const [tutors, setTutors] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [filters, setFilters] = useState({
    subject: '',
    location: '',
    maxRate: '',
    minRate: '',
    level: '',
    availability: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalTutors: 0,
    avgRating: 0,
    avgRate: 0,
    featuredTutors: 0
  });

  const subjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology',
    'English', 'French', 'History', 'Geography',
    'Computer Science', 'Economics', 'Accounting',
    'Further Mathematics', 'Literature'
  ];

  const levels = [
    'Primary', 'Form 1-2', 'Form 3-5', 'Lower Sixth',
    'Upper Sixth', 'University', 'Professional'
  ];

  const cities = [
    'All Locations', 'Yaoundé', 'Douala', 'Bamenda', 'Buea', 'Limbe',
    'Bafoussam', 'Garoua', 'Maroua', 'Ngaoundéré', 'Kumba'
  ];

  const availabilityOptions = [
    'All', 'Weekdays', 'Weekends', 'Morning', 'Afternoon', 'Evening'
  ];

  const sortOptions = [
    { value: 'rating', label: 'Highest Rating', icon: <FaStar /> },
    { value: 'rate', label: 'Lowest Rate', icon: <FaMoneyBill /> },
    { value: 'sessions', label: 'Most Experienced', icon: <FaUsers /> },
    { value: 'name', label: 'Name A-Z', icon: <FaUserGraduate /> }
  ];

  useEffect(() => {
    fetchAllTutors();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [filters, tutors, searchQuery, sortBy]);

  const calculateStats = () => {
    if (tutors.length === 0) return;
    
    const totalRating = tutors.reduce((sum, tutor) => sum + (tutor.rating || 0), 0);
    const totalRate = tutors.reduce((sum, tutor) => sum + (tutor.hourlyRate || 0), 0);
    const featured = tutors.filter(t => t.rating >= 4.5 && t.totalSessions >= 20).length;
    
    setStats({
      totalTutors: tutors.length,
      avgRating: (totalRating / tutors.length).toFixed(1),
      avgRate: Math.round(totalRate / tutors.length),
      featuredTutors: featured
    });
  };

  const fetchAllTutors = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Starting to fetch all tutors...');

      const usersQuery = query(collection(db, 'users'), where('userType', '==', 'tutor'));
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.size === 0) {
        console.log('No tutors found in users collection, trying tutors collection...');
        await fetchFromTutorsCollection();
        return;
      }

      const tutorsData = [];
      const tutorsPromises = [];

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        
        const promise = (async () => {
          try {
            const tutorQuery = query(collection(db, 'tutors'), where('uid', '==', userData.uid || doc.id));
            const tutorSnapshot = await getDocs(tutorQuery);
            
            let mergedData = { 
              id: doc.id, 
              uid: userData.uid || doc.id,
              ...userData 
            };
            
            if (!tutorSnapshot.empty) {
              const tutorData = tutorSnapshot.docs[0].data();
              mergedData = { ...mergedData, ...tutorData };
              
              if (!mergedData.status && userData.status) {
                mergedData.status = userData.status;
              } else if (!mergedData.status) {
                mergedData.status = 'approved';
              }
            } else {
              mergedData = {
                ...mergedData,
                subjects: userData.subjects || [],
                hourlyRate: userData.hourlyRate || 15,
                rating: userData.rating || 0,
                totalSessions: userData.totalSessions || 0,
                studentsHelped: Math.floor(Math.random() * 50) + 10, // Mock data
                responseRate: ['Fast', 'Medium', 'Slow'][Math.floor(Math.random() * 3)], // Mock data
                availability: ['Available Today', 'Available This Week', 'Limited'][Math.floor(Math.random() * 3)], // Mock data
                status: userData.status || 'approved',
                profileComplete: userData.profileComplete || false
              };
            }
            
            return mergedData;
          } catch (err) {
            console.error('Error fetching tutor details:', err);
            return { 
              id: doc.id, 
              uid: userData.uid || doc.id,
              ...userData,
              subjects: userData.subjects || [],
              hourlyRate: userData.hourlyRate || 15,
              rating: userData.rating || 0,
              totalSessions: userData.totalSessions || 0,
              status: userData.status || 'approved'
            };
          }
        })();
        
        tutorsPromises.push(promise);
      });

      const allTutors = await Promise.all(tutorsPromises);
      const approvedTutors = allTutors.filter(tutor => 
        tutor.status === 'approved' || tutor.status === undefined
      );
      
      console.log(`Total tutors: ${allTutors.length}, Approved: ${approvedTutors.length}`);
      
      if (approvedTutors.length === 0) {
        console.log('No approved tutors found, showing all tutors for debugging...');
        setTutors(allTutors);
        setFilteredTutors(allTutors);
      } else {
        setTutors(approvedTutors);
        setFilteredTutors(approvedTutors);
      }

    } catch (error) {
      console.error('Error fetching tutors:', error);
      setError(`Error loading tutors: ${error.message}`);
      
      // Mock data for testing
      setTutors(generateMockTutors());
    } finally {
      setLoading(false);
    }
  };

  const generateMockTutors = () => {
    const mockTutors = [];
    const names = ['John Doe', 'Jane Smith', 'Michael Johnson', 'Sarah Williams', 'David Brown', 'Lisa Davis'];
    const locations = ['Yaoundé', 'Douala', 'Bamenda', 'Buea', 'Limbe', 'Bafoussam'];
    const allSubjects = subjects.slice(0, 6);
    
    for (let i = 0; i < 6; i++) {
      const subjectsCount = Math.floor(Math.random() * 3) + 1;
      const tutorSubjects = [];
      for (let j = 0; j < subjectsCount; j++) {
        const subject = allSubjects[Math.floor(Math.random() * allSubjects.length)];
        if (!tutorSubjects.includes(subject)) {
          tutorSubjects.push(subject);
        }
      }
      
      mockTutors.push({
        id: i.toString(),
        uid: i.toString(),
        fullName: names[i],
        email: `${names[i].toLowerCase().replace(' ', '.')}@example.com`,
        location: locations[Math.floor(Math.random() * locations.length)],
        subjects: tutorSubjects,
        hourlyRate: Math.floor(Math.random() * 20) + 15,
        rating: (Math.random() * 2 + 3).toFixed(1),
        totalSessions: Math.floor(Math.random() * 100) + 20,
        studentsHelped: Math.floor(Math.random() * 50) + 10,
        responseRate: ['Fast', 'Medium', 'Slow'][Math.floor(Math.random() * 3)],
        availability: ['Available Today', 'Available This Week', 'Limited'][Math.floor(Math.random() * 3)],
        status: 'approved',
        profileComplete: true
      });
    }
    
    return mockTutors;
  };

  const fetchFromTutorsCollection = async () => {
    try {
      const tutorsSnapshot = await getDocs(collection(db, 'tutors'));
      
      if (tutorsSnapshot.size === 0) return;

      const tutorsData = [];
      const tutorsPromises = [];

      tutorsSnapshot.forEach((doc) => {
        const tutorData = doc.data();
        
        const promise = (async () => {
          try {
            const userQuery = query(collection(db, 'users'), where('uid', '==', tutorData.uid || doc.id));
            const userSnapshot = await getDocs(userQuery);
            
            let mergedData = { 
              id: doc.id, 
              uid: tutorData.uid || doc.id,
              ...tutorData 
            };
            
            if (!userSnapshot.empty) {
              const userData = userSnapshot.docs[0].data();
              mergedData = { ...mergedData, ...userData };
            }
            
            return mergedData;
          } catch (err) {
            console.error('Error fetching user data:', err);
            return { 
              id: doc.id, 
              uid: tutorData.uid || doc.id,
              ...tutorData 
            };
          }
        })();
        
        tutorsPromises.push(promise);
      });

      const allTutors = await Promise.all(tutorsPromises);
      const approvedTutors = allTutors.filter(tutor => 
        tutor.status === 'approved' || tutor.status === undefined
      );
      
      if (approvedTutors.length > 0) {
        setTutors(approvedTutors);
        setFilteredTutors(approvedTutors);
      }
    } catch (error) {
      console.error('Error fetching from tutors collection:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...tutors];

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tutor => {
        const nameMatch = tutor.fullName?.toLowerCase().includes(query);
        const locationMatch = tutor.location?.toLowerCase().includes(query);
        const subjectMatch = tutor.subjects?.some(subject => 
          subject?.toLowerCase().includes(query)
        );
        const bioMatch = tutor.bio?.toLowerCase().includes(query);
        
        return nameMatch || locationMatch || subjectMatch || bioMatch;
      });
    }

    // Apply subject filter
    if (filters.subject && filters.subject !== 'All Subjects') {
      filtered = filtered.filter(tutor => 
        tutor.subjects?.includes(filters.subject)
      );
    }

    // Apply location filter
    if (filters.location && filters.location !== 'All Locations') {
      filtered = filtered.filter(tutor => 
        tutor.location?.toLowerCase() === filters.location.toLowerCase()
      );
    }

    // Apply rate filters
    if (filters.minRate) {
      filtered = filtered.filter(tutor => 
        tutor.hourlyRate >= parseFloat(filters.minRate)
      );
    }

    if (filters.maxRate) {
      filtered = filtered.filter(tutor => 
        tutor.hourlyRate <= parseFloat(filters.maxRate)
      );
    }

    // Apply level filter
    if (filters.level && filters.level !== 'All Levels') {
      filtered = filtered.filter(tutor => 
        tutor.levels?.includes(filters.level)
      );
    }

    // Apply availability filter
    if (filters.availability && filters.availability !== 'All') {
      filtered = filtered.filter(tutor => {
        if (!tutor.availability || typeof tutor.availability !== 'string') return false;
        
        return tutor.availability.toLowerCase().includes(filters.availability.toLowerCase());
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'rate':
          return (a.hourlyRate || 0) - (b.hourlyRate || 0);
        case 'sessions':
          return (b.totalSessions || 0) - (a.totalSessions || 0);
        case 'name':
          return (a.fullName || '').localeCompare(b.fullName || '');
        default:
          return 0;
      }
    });

    setFilteredTutors(filtered);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const resetFilters = () => {
    setFilters({
      subject: '',
      location: '',
      maxRate: '',
      minRate: '',
      level: '',
      availability: ''
    });
    setSearchQuery('');
    setFilteredTutors(tutors);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  return (
    <>
      <Navbar />
      <div className="search-page">
        {/* Hero Section */}
        <section className="search-hero animate-fade-in">
          <div className="container">
            <div className="search-hero-content">
              <h1 className="search-hero-title">
                Find Your Perfect <span className="text-gradient">Tutor</span>
              </h1>
              <p className="search-hero-subtitle">
                Connect with qualified tutors across Cameroon. Learn at your own pace.
              </p>
              
              <div className="search-bar animate-fade-in-up">
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="search-bar-input"
                    placeholder="Search by subject, tutor name, or keyword..."
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                </div>
                <button 
                  className="btn btn-outline filter-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FaFilter /> {showFilters ? 'Hide Filters' : 'Show Filters'} 
                  {showFilters ? <FaCaretUp /> : <FaCaretDown />}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="stats-bar animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="container">
            <div className="stats-grid">
              <div className="stat-card hover-lift">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3>{stats.totalTutors}</h3>
                  <p>Total Tutors</p>
                </div>
              </div>
              <div className="stat-card hover-lift">
                <div className="stat-icon">
                  <FaStar />
                </div>
                <div className="stat-content">
                  <h3>{stats.avgRating}</h3>
                  <p>Avg Rating</p>
                </div>
              </div>
              <div className="stat-card hover-lift">
                <div className="stat-icon">
                  <FaMoneyBill />
                </div>
                <div className="stat-content">
                  <h3>${stats.avgRate}</h3>
                  <p>Avg Rate/Hour</p>
                </div>
              </div>
              <div className="stat-card hover-lift">
                <div className="stat-icon">
                  <FaCheckCircle />
                </div>
                <div className="stat-content">
                  <h3>{stats.featuredTutors}</h3>
                  <p>Featured Tutors</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container">
          {error && (
            <div className="error-message animate-fade-in-down">
              <FaTimes style={{ marginRight: '10px' }} />
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="search-layout">
            {/* Filters Sidebar */}
            <aside className={`filters-sidebar ${showFilters ? 'active' : ''} animate-fade-in-left`}>
              <div className="filters-header">
                <h3>
                  <FaFilter /> Advanced Filters
                  <span className="badge" style={{ 
                    background: 'var(--primary-500)', 
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    marginLeft: '8px'
                  }}>
                    {Object.values(filters).filter(f => f).length} active
                  </span>
                </h3>
                <button className="btn-text" onClick={resetFilters}>
                  Clear All
                </button>
              </div>
              
              <div className="filter-section">
                <div className="filter-header">
                  <h4><FaGraduationCap /> Subjects</h4>
                </div>
                <select 
                  name="subject" 
                  className="filter-select"
                  value={filters.subject}
                  onChange={handleFilterChange}
                >
                  <option value="">All Subjects</option>
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-header">
                  <h4><FaMapMarkerAlt /> Location</h4>
                </div>
                <select 
                  name="location" 
                  className="filter-select"
                  value={filters.location}
                  onChange={handleFilterChange}
                >
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-header">
                  <h4>Academic Level</h4>
                </div>
                <select 
                  name="level" 
                  className="filter-select"
                  value={filters.level}
                  onChange={handleFilterChange}
                >
                  <option value="">All Levels</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-header">
                  <h4><FaClock /> Availability</h4>
                </div>
                <select 
                  name="availability" 
                  className="filter-select"
                  value={filters.availability}
                  onChange={handleFilterChange}
                >
                  {availabilityOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-section">
                <div className="filter-header">
                  <h4><FaMoneyBill /> Hourly Rate</h4>
                </div>
                <div className="rate-range">
                  <div className="rate-input-group">
                    <span className="rate-label">Min: $</span>
                    <input
                      type="number"
                      name="minRate"
                      className="filter-input"
                      placeholder="0"
                      value={filters.minRate}
                      onChange={handleFilterChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <span className="rate-separator">-</span>
                  <div className="rate-input-group">
                    <span className="rate-label">Max: $</span>
                    <input
                      type="number"
                      name="maxRate"
                      className="filter-input"
                      placeholder="100"
                      value={filters.maxRate}
                      onChange={handleFilterChange}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              </div>
              
              <button className="btn btn-primary btn-full apply-filters" onClick={applyFilters}>
                Apply Filters
              </button>
              
              <div className="filter-tags">
                {Object.entries(filters).map(([key, value]) => 
                  value && (
                    <span key={key} className="filter-tag">
                      {key}: {value}
                      <button 
                        onClick={() => setFilters({...filters, [key]: ''})}
                        className="tag-remove"
                      >
                        ×
                      </button>
                    </span>
                  )
                )}
              </div>
            </aside>
            
            {/* Results Section */}
            <main className="results-section animate-fade-in-right">
              <div className="results-header">
                <div className="results-title-section">
                  <h2>
                    Available Tutors 
                    <span className="results-count"> ({filteredTutors.length} found)</span>
                  </h2>
                  <p className="results-subtitle">
                    <FaUserGraduate /> Verified tutors ready to help you learn
                  </p>
                </div>
                <div className="sort-options">
                  <label htmlFor="sort-select" className="sort-label">
                    <FaSort /> Sort by:
                  </label>
                  <div className="select-wrapper">
                    <select 
                      id="sort-select" 
                      className="sort-select" 
                      value={sortBy} 
                      onChange={handleSortChange}
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <FaCaretDown className="select-arrow" />
                  </div>
                </div>
              </div>
              
              {loading ? (
                <div className="loading-state animate-fade-in">
                  <div className="loading-spinner"></div>
                  <p>Finding the best tutors for you...</p>
                </div>
              ) : filteredTutors.length > 0 ? (
                <>
                  <div className="tutors-grid">
                    {filteredTutors.map((tutor, index) => (
                      <div 
                        key={tutor.id || tutor.uid} 
                        className="tutor-card-wrapper"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <TutorCard tutor={tutor} />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty-state animate-fade-in">
                  <div className="empty-state-icon">
                    <FaSearch />
                  </div>
                  <h3>No tutors found</h3>
                  <p>{tutors.length === 0 
                    ? 'No tutors are currently available. Please check back later.' 
                    : 'No tutors match your current filters. Try adjusting your search criteria.'}
                  </p>
                  <div className="empty-state-actions">
                    <button className="btn btn-primary" onClick={resetFilters}>
                      Reset All Filters
                    </button>
                    <button 
                      className="btn btn-outline" 
                      onClick={fetchAllTutors}
                    >
                      Refresh Tutors
                    </button>
                  </div>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorSearch;